/**
 * Content Editor list page — Concept B layout (cards grouped by folder).
 *
 * What changed vs. the prior table layout:
 *   • Removed: FolderSidebar (folders are now inline section headings),
 *     FolderHeroBanner (no folder-scoped view), EditorModuleTable
 *     (replaced by EditorFolderGroups card grid).
 *   • Always fetches all modules in the org (no folder-scoped query) —
 *     grouping happens client-side. At 200 modules per folder, that's
 *     still well under the 1 MB JSON budget. Above that we'd need to
 *     paginate per-folder, but we're nowhere near.
 *   • Per-folder actions (rename/delete/move/new subfolder) live in the
 *     kebab on each folder header. Per-folder upload is a hover-revealed
 *     "+ add module" link in the folder header.
 *   • Status filter (All / Draft / Published / Archived) sits between
 *     the page header and the folder groups — unchanged component.
 *   • Bulk action bar at the bottom of the page is unchanged.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useApi } from '../hooks/useApi';
import { useFolders } from '../hooks/useFolders';
import { useAuth } from '../contexts/AuthContext';
import { EmptyState } from '../components/common/EmptyState';
import { SkeletonCard } from '../components/common/SkeletonCard';
import { Button } from '../components/ui/button';
import { FolderFormDialog } from '../components/folders/FolderFormDialog';
import { FolderDeleteDialog } from '../components/folders/FolderDeleteDialog';
import { MoveToFolderDialog } from '../components/folders/MoveToFolderDialog';
import { BookOpen, Upload, FolderPlus } from 'lucide-react';
import { EditorPageHeader, EditorStatusTabs } from './editor/EditorHeader';
import { EditorFolderGroups } from './editor/EditorFolderGroups';
import { BulkActionBar } from './editor/BulkActionBar';
import { DeleteModuleDialog, BulkDeleteDialog } from './editor/DeleteDialogs';
import { useModuleSelection } from './editor/useModuleSelection';

export default function EditorModuleListPage() {
  const { get, del, post, patch } = useApi();
  const { user, hasRole } = useAuth();
  const folders = useFolders();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';
  // Trainers get read-only access (Feb 2026 role-split). Editor+ keeps full
  // authoring controls. Backend `require_editor` blocks writes regardless,
  // but hiding the affordances client-side avoids dead-end clicks.
  const canEdit = hasRole('editor');

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Folder management dialog state — same dialogs as the old layout, just
  // triggered from the per-folder kebab in EditorFolderGroups now.
  const [folderForm, setFolderForm] = useState({ open: false, mode: 'create', parentId: null, initial: null });
  const [folderDelete, setFolderDelete] = useState(null);
  const [moveModuleDialog, setMoveModuleDialog] = useState({ open: false, ids: [] });
  const [moveFolderDialog, setMoveFolderDialog] = useState(null);

  const refreshModules = useCallback(() => {
    // No folder-scoped filter — fetch the whole org and group client-side.
    // limit=500 covers any realistic org size; we'll add real pagination
    // if a Lumyn customer ever crosses that line.
    return get('/modules?limit=500')
      .then(res => setModules(res.modules || []))
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to load modules');
        setModules([]);
      });
  }, [get]);

  useEffect(() => {
    refreshModules().finally(() => setLoading(false));
  }, [refreshModules]);

  const filtered = useMemo(
    () => (tab === 'all' ? modules : modules.filter(m => m.status === tab)),
    [tab, modules],
  );
  const visibleIds = useMemo(() => filtered.map(m => m.module_id), [filtered]);

  const {
    selectedIds, selectedModules, selectedCount, bulkImpact,
    clearSelection, toggleOne,
  } = useModuleSelection(modules, visibleIds);

  const handleDeleteOne = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await del(`/modules/${pendingDelete.module_id}`);
      toast.success(`Deleted "${pendingDelete.title}"`);
      setPendingDelete(null);
      await refreshModules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCount === 0) return;
    setDeleting(true);
    try {
      const ids = selectedModules.map(m => m.module_id);
      const res = await post('/modules/bulk-delete', { module_ids: ids });
      const { deleted_count, failed_count } = res || {};
      if (failed_count > 0) toast.warning(`Deleted ${deleted_count} of ${ids.length} — ${failed_count} failed`);
      else toast.success(`Deleted ${deleted_count} module${deleted_count === 1 ? '' : 's'}`);
      clearSelection();
      setBulkConfirmOpen(false);
      await refreshModules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleMoveModules = async (folderId) => {
    const ids = moveModuleDialog.ids;
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        await patch(`/modules/${ids[0]}/move`, { folder_id: folderId });
      } else {
        await post('/modules/bulk-move', { module_ids: ids, folder_id: folderId });
      }
      toast.success(`Moved ${ids.length} module${ids.length === 1 ? '' : 's'}`);
      setMoveModuleDialog({ open: false, ids: [] });
      clearSelection();
      await Promise.all([refreshModules(), folders.refresh()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Move failed');
    }
  };

  // Drag-and-drop quick-move: invoked when a module card is dropped onto a
  // folder header. Bypasses the MoveToFolderDialog and silently calls the
  // single or bulk move endpoint. A no-op (same-folder) is still toasted as
  // "Already there" so the user knows the drop registered.
  const handleQuickMove = useCallback(async (moduleIds, folderId) => {
    const ids = (moduleIds || []).filter(Boolean);
    if (!ids.length) return;
    // Same-folder fast path — don't fire a backend call.
    const currentFolders = new Set(
      modules.filter(m => ids.includes(m.module_id)).map(m => m.folder_id ?? null),
    );
    if (currentFolders.size === 1 && currentFolders.has(folderId ?? null)) {
      toast.info(ids.length === 1 ? 'Already in this folder' : 'All selected modules already in this folder');
      return;
    }
    try {
      if (ids.length === 1) {
        await patch(`/modules/${ids[0]}/move`, { folder_id: folderId });
      } else {
        await post('/modules/bulk-move', { module_ids: ids, folder_id: folderId });
      }
      toast.success(`Moved ${ids.length} module${ids.length === 1 ? '' : 's'}`);
      clearSelection();
      await Promise.all([refreshModules(), folders.refresh()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Move failed');
    }
  }, [modules, patch, post, clearSelection, refreshModules, folders]);

  const handleFolderSubmit = async (payload) => {
    if (folderForm.mode === 'create') {
      await folders.create({ ...payload, parent_folder_id: folderForm.parentId });
    } else if (folderForm.initial) {
      await folders.update(folderForm.initial.folder_id, payload);
    }
    setFolderForm({ open: false, mode: 'create', parentId: null, initial: null });
  };

  const handleFolderDelete = async (mode) => {
    if (!folderDelete) return;
    await folders.remove(folderDelete.folder_id, mode);
    setFolderDelete(null);
    await refreshModules();
  };

  const handleFolderMove = async (newParentId) => {
    if (!moveFolderDialog) return;
    await folders.moveFolder(moveFolderDialog.folder_id, newParentId);
    setMoveFolderDialog(null);
  };

  // Per-folder "+ add module" → drops the user on the Upload page with
  // the folder pre-selected. Null/undefined → general upload page.
  const handleAddModuleToFolder = (folderId) => {
    if (folderId) navigate(`/editor/modules/upload?folder_id=${folderId}`);
    else navigate('/editor/modules/upload');
  };

  const totalCount = modules.length;

  return (
    <div className="space-y-6 pb-24 relative" data-testid="editor-module-list">
      <div aria-hidden className="page-blob bg-[var(--lumyn-content)]" />
      <div className="relative z-10 space-y-6">
        {/* Header — keeps the global "Upload presentation" + "Blank module"
            CTAs for the no-folder case. Per-folder upload still lives in
            the folder header. `selectedFolder='all'` because we no longer
            track a single selection. */}
        <EditorPageHeader totalCount={totalCount} selectedFolder="all" />

        {/* Status filter row — unchanged component, lives directly below
            the header now that the folder column is gone. */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <EditorStatusTabs tab={tab} onChange={setTab} modules={modules} />
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFolderForm({ open: true, mode: 'create', parentId: null, initial: null })}
              data-testid="new-folder-btn"
            >
              <FolderPlus className="w-4 h-4 mr-1.5" />
              New folder
            </Button>
          )}
        </div>

        {loading || folders.loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array(6).fill(0).map((_, i) => <SkeletonCard key={`skel-${i}`} />)}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="No modules"
            description={canEdit
              ? 'Upload your first presentation or create a blank module to get started.'
              : 'No modules have been published yet. Check back once your editor uploads some.'}
            action={canEdit ? (
              <Button asChild>
                <Link to="/editor/modules/upload">
                  <Upload className="w-4 h-4 mr-2" />Upload
                </Link>
              </Button>
            ) : null}
          />
        ) : (
          <EditorFolderGroups
            modules={filtered}
            folderTree={folders.tree}
            selectedIds={selectedIds}
            canEdit={canEdit}
            onToggleModule={canEdit ? toggleOne : null}
            onMoveModule={canEdit ? ((id) => setMoveModuleDialog({ open: true, ids: [id] })) : null}
            onQuickMove={canEdit ? handleQuickMove : null}
            onDeleteModule={canEdit ? ((m) => setPendingDelete(m)) : null}
            onAddModuleToFolder={canEdit ? handleAddModuleToFolder : null}
            onEditFolder={canEdit ? ((f) => setFolderForm({ open: true, mode: 'edit', parentId: null, initial: f })) : null}
            onDeleteFolder={canEdit ? ((f) => setFolderDelete(f)) : null}
            onMoveFolder={canEdit ? ((f) => setMoveFolderDialog(f)) : null}
            onCreateSubfolder={canEdit ? ((parentId) => setFolderForm({ open: true, mode: 'create', parentId, initial: null })) : null}
          />
        )}

        {canEdit && (
          <BulkActionBar
            selectedCount={selectedCount}
            onClear={clearSelection}
            onMove={() => setMoveModuleDialog({ open: true, ids: selectedModules.map(m => m.module_id) })}
            onDelete={() => setBulkConfirmOpen(true)}
          />
        )}

        <DeleteModuleDialog
          pendingDelete={pendingDelete}
          deleting={deleting}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleDeleteOne}
        />

        <BulkDeleteDialog
          open={bulkConfirmOpen}
          onOpenChange={setBulkConfirmOpen}
          deleting={deleting}
          selectedCount={selectedCount}
          selectedModules={selectedModules}
          bulkImpact={bulkImpact}
          onConfirm={handleBulkDelete}
        />

        <FolderFormDialog
          open={folderForm.open}
          onOpenChange={(o) => setFolderForm(f => ({ ...f, open: o }))}
          mode={folderForm.mode}
          parentName={folderForm.parentId ? folders.findById(folderForm.parentId)?.name : null}
          initial={folderForm.initial}
          onSubmit={handleFolderSubmit}
        />
        <FolderDeleteDialog
          open={!!folderDelete}
          onOpenChange={(o) => !o && setFolderDelete(null)}
          folder={folderDelete}
          isSuperAdmin={isSuperAdmin}
          onConfirm={handleFolderDelete}
        />
        <MoveToFolderDialog
          open={moveModuleDialog.open}
          onOpenChange={(o) => setMoveModuleDialog(s => ({ ...s, open: o }))}
          tree={folders.tree}
          title={moveModuleDialog.ids.length === 1 ? 'Move module to folder' : `Move ${moveModuleDialog.ids.length} modules to folder`}
          currentId={null}
          allowRoot
          onConfirm={handleMoveModules}
        />
        <MoveToFolderDialog
          open={!!moveFolderDialog}
          onOpenChange={(o) => !o && setMoveFolderDialog(null)}
          tree={folders.tree}
          title={moveFolderDialog ? `Move "${moveFolderDialog.name}" to…` : 'Move folder'}
          currentId={moveFolderDialog?.parent_folder_id || null}
          excludeIds={moveFolderDialog ? folders.descendantsOf(moveFolderDialog.folder_id) : null}
          allowRoot
          onConfirm={handleFolderMove}
        />
      </div>
    </div>
  );
}
